import type { SelectOption } from 'naive-ui'
import { inject, type ComputedRef, type Ref } from 'vue'
import type { AxiosInstance } from 'axios'

import { fetchAll } from '@lib/util/network'
import type { PitcherInfo } from '@lib/types/app'
import type { User } from '@openapi'
import type { PaginatedData } from '@lib/types/paginatedData'

export type Owner = {
  value: User['id']
  name: string
  email: User['email']
}

function insertUserSorted(users: Owner[], newUser: Owner) {
  const index = users.findIndex((u) => (u.name ?? '').toLowerCase() > newUser.name.toLowerCase())
  if (index === -1) users.push(newUser)
  else users.splice(index, 0, newUser)

  return users
}

function filterUsers(searchTerm: string | null | undefined, option: SelectOption) {
  const name = (option.name ?? '') as string
  const email = (option.email ?? '') as string
  const search = (searchTerm ?? '').toLowerCase()
  return name.toLowerCase().includes(search) || email.toLowerCase().includes(search)
}

export default function useFetchUsers() {
  const pitcherInfo = inject<ComputedRef<PitcherInfo>>('pitcherInfo')
  const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')

  function check() {
    if (!pitcherInfo?.value) {
      throw new Error('Pitcher info not available')
    }
    if (!restApiAxios?.value) {
      throw new Error('HTTP client is not initialized')
    }
  }

  async function fetchUsers(myUser?: User | null) {
    try {
      check()
      return await fetchAll<User>(
        async (params: any) => {
          check()
          return await restApiAxios!.value!.get<unknown, PaginatedData<User>>(`users/`, {
            params,
          })
        },
        { instance_id: pitcherInfo!.value!.instanceId },
      ).then(async (r) => {
        const users = (r ?? []).reduce((acc, user) => {
          const computedName = user.name ? user.name.trim() : user.email
          const newUser = {
            name: computedName,
            email: user.email,
            value: user.id,
          }

          insertUserSorted(acc, newUser)
          return acc
        }, [] as Owner[])

        if (myUser) {
          const me = { name: myUser.name, value: myUser.id, email: myUser.email }
          !users.find((user) => user.value === me.value) && insertUserSorted(users, me)
        }

        return users
      })
    } catch (error) {
      console.error('Error fetching users:', error)
      return []
    }
  }

  return { fetchUsers, filterUsers, insertUserSorted }
}
